import { useEffect, useState } from "react";
import { api } from "../api";
import type { Share } from "../types";
import { formatDate } from "../lib/formatDate";
import ModalBackdrop from "./ui/ModalBackdrop";

interface Props {
  caseId: string;
  onClose: () => void;
}

function shareUrl(token: string): string {
  // Public link served by the SPA; API is called from within it.
  const origin =
    typeof window !== "undefined"
      ? window.location.origin
      : "https://casethread.app";
  return `${origin}/share/${token}`;
}

export default function ShareManager({ caseId, onClose }: Props) {
  const [shares, setShares] = useState<Share[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  async function refresh() {
    setLoading(true);
    try {
      const { shares } = await api.listShares(caseId);
      setShares(shares);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId]);

  async function createShare() {
    setCreating(true);
    setError(null);
    try {
      await api.createShare(caseId);
      await refresh();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setCreating(false);
    }
  }

  async function revoke(id: string) {
    if (!window.confirm("Revoke this link? Anyone holding it will lose access."))
      return;
    try {
      await api.revokeShare(id);
      await refresh();
    } catch (err) {
      window.alert((err as Error).message);
    }
  }

  async function copy(token: string) {
    const url = shareUrl(token);
    try {
      await navigator.clipboard.writeText(url);
      setCopied(token);
      window.setTimeout(() => setCopied(null), 1500);
    } catch {
      window.prompt("Copy this link:", url);
    }
  }

  function isActive(s: Share): boolean {
    if (s.revoked_at) return false;
    return new Date(s.expires_at).getTime() > Date.now();
  }
  function statusLabel(s: Share): string {
    if (s.revoked_at) return "Revoked";
    if (!isActive(s)) return "Expired";
    const days = Math.ceil(
      (new Date(s.expires_at).getTime() - Date.now()) /
        (24 * 60 * 60 * 1000),
    );
    return `Active — ${days} day${days === 1 ? "" : "s"} left`;
  }

  return (
    <ModalBackdrop onClose={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="share-title"
        className="share-card"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="share-header">
          <h2 id="share-title" className="modal-title">
            Share this case
          </h2>
          <button
            type="button"
            className="btn-icon"
            onClick={onClose}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <p className="muted">
          A read-only link — no editing, no deletion, no file downloads
          beyond the view. Expires after 30 days. You can revoke at any time.
        </p>

        <div className="share-actions">
          <button
            type="button"
            className="btn-primary"
            onClick={createShare}
            disabled={creating}
          >
            {creating ? "Creating…" : "+ New share link"}
          </button>
        </div>

        {loading ? (
          <div className="muted">Loading…</div>
        ) : shares.length === 0 ? (
          <div className="empty-state">
            <p>No active links.</p>
          </div>
        ) : (
          <ul className="share-list">
            {shares.map((s) => (
              <li
                key={s.id}
                className={`share-item${isActive(s) ? "" : " inactive"}`}
              >
                <div className="share-item-main">
                  <code className="share-url">{shareUrl(s.token)}</code>
                  <div className="share-meta muted">
                    Created {formatDate(s.created_at, "short")} ·{" "}
                    {statusLabel(s)}
                  </div>
                </div>
                <div className="share-item-actions">
                  {isActive(s) && (
                    <button
                      type="button"
                      onClick={() => copy(s.token)}
                    >
                      {copied === s.token ? "Copied!" : "Copy"}
                    </button>
                  )}
                  {isActive(s) && (
                    <button
                      type="button"
                      className="btn-danger-subtle"
                      onClick={() => revoke(s.id)}
                    >
                      Revoke
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}

        {error && <div className="form-error">{error}</div>}
      </div>
    </ModalBackdrop>
  );
}
